import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { Municipality } from '@ta-interaktiv/react-municipality-search'
import ChoroplethMap from './choroplethMap'
import styled from 'styled-components'
import { useWindowSize } from 'usehooks-ts'
import {
  ContentWidth,
  TextWidthExtended,
} from '@ta-interaktiv/react-disco-grid'
import { LayerStyles, ToolTipProps } from 'Types/MicroFrontendProps'

export interface Props {
  colorMunicipality: (e: any) => string
  /** The key of the property you want to compare over the municipalities (e.g. "taxBurden") */
  propertyNameToCompare?: string
  /** If the property we are comparing is an array we need a index to what value we want to compare it in te array */
  indexOfPropertyToCompare?: number
  /** Hide the search bar */
  hideMunicipalitySearch?: boolean

  /** The dataset used for the municipality search bar. Go to the s3 bucket: interaktiv-tenant-source/static/gemeindesuche/ to see the available datasets (e.g. "2020") */
  municipalitySearchYear?: string
  /** The placeholder of the municipality search bar default is 'Gemeindesuche' and 'Nom de la commune' */
  municipalitySearchPlaceholder?: string
  /** Pass a custom array of municipalities for the municipality search */
  municipalitySearchCustomMunicipalities?: Municipality[]
  /** The year of the topoJson that will be fetched and used to draw the map. Go to https://swiss-maps.vercel.app/ to see the available datasets */
  topoJsonYear: number
  /** If you want to use your own (local) topoJson you can pass it here. Go to https://swiss-maps.vercel.app/ to see the available datasets */
  topoJson?: any

  /** This function gets triggered when the mouse hovers over a municipality. The municipalityData object of the currently hovered municipality will be passed to the function (e.g. {bfsId: 261, taxBurden: "7"}) */
  hoverHandler?: (newData: any) => void
  /** show the zoom controls */
  isZoomable?: boolean
  /** zoom to the currently selected municipality */
  zoomIntoActiveMunicipality?: boolean
  /** disable touch events on 600px width or smaller */
  disableTouchOnMobile?: boolean
  /** To show a tooltip when hovering you need to pass a tooltip component. The municipalityData object of the currently hovered municipality will be passed as a prop (e.g. {bfsId: 261, taxBurden: "7"}) */
  ToolTip?: FC<ToolTipProps>

  /** stroke-width of active municipality */
  activeStrokeWidth?: string
  /** stroke-color of active municipality */
  activeStrokeColor?: string

  /** lang of component */
  locale?: 'de' | 'fr'
  /** event handler for fullscreen state change */
  fullscreenChangeHandler?: (isFullscreen: boolean) => void
  /** is the map in fullscreen mode or not */
  isFullscreen?: boolean
  /** custom legend component */
  LegendComponent?: any
  /** scroll map into view when new municipality is selected */
  scrollIntoView?: boolean
  sort?: 'rel' | 'abs'
  data: any
  onBfsIdChange?: (bfsId: number) => void
  layerStyles?: LayerStyles
  activeMunicipalityId: number | undefined
  setActiveMunicipalityId: (bfsId: number | undefined) => void
  colorMode?: 'light' | 'dark' | undefined
}

export const SwissMap: FC<Props> = ({
  colorMunicipality,
  indexOfPropertyToCompare,
  topoJsonYear = 2019,
  topoJson,
  isZoomable = false,
  disableTouchOnMobile,
  ToolTip,
  activeStrokeWidth = '2',
  activeStrokeColor = '#A757A3',
  isFullscreen,
  scrollIntoView,
  layerStyles,
  onBfsIdChange,
  colorMode,
  activeMunicipalityId = 0,
  setActiveMunicipalityId,
  data,
}): ReactElement => {
  const [toolTipData, setToolTipData] = useState<any | null>()
  const windowSize = useWindowSize()
  const isMobile = windowSize.width < 712
  const toolTipRef = React.useRef<HTMLDivElement>(null)
  const staticRef = React.useRef<HTMLDivElement>(null)
  const [activeData, setActiveMuniData] = useState<any>()

  function newActiveMunicipalityIdHandler(muniId: number) {
    if (onBfsIdChange) onBfsIdChange(muniId)
    setActiveMunicipalityId(+(muniId + ''))
  }
  function handleHover(newData: any) {
    if (ToolTip) {
      setToolTipData(newData)
    }
  }

  useEffect(() => {
    if (!data) return
    if (!activeMunicipalityId) activeMunicipalityId = 0
    var d =
      data.find(
        (m: any) =>
          +m.bfsId === activeMunicipalityId || m.bfsId === activeMunicipalityId
      ) || undefined
    setActiveMuniData(d)
  }, [activeMunicipalityId, data])

  return (
    <>
      <MapWrapper
        className='swiss-map'
        style={{
          zIndex: 401,
        }}
      >
        {data && (
          <ChoroplethMap
            onZoomOut={() => {
              !isMobile && setActiveMunicipalityId(undefined)
            }}
            layerStyles={layerStyles}
            municipalityData={data}
            indexOfPropertyToCompare={indexOfPropertyToCompare}
            year={topoJsonYear}
            topoJson={topoJson}
            activeMunicipalityId={activeMunicipalityId}
            newActiveMunicipalityIdHandler={newActiveMunicipalityIdHandler}
            color={colorMunicipality}
            hoverHandler={handleHover}
            isZoomable={isZoomable}
            zoomIntoActiveMunicipality={true}
            disableTouchOnMobile={disableTouchOnMobile}
            activeStrokeWidth={activeStrokeWidth}
            activeStrokeColor={activeStrokeColor}
            toolTipRef={toolTipRef}
            municipalityFallbackFillColor='var(--site-background)'
            colorMode={colorMode}
            isFullscreen={isFullscreen}
            scrollIntoView={scrollIntoView}
          />
        )}
        {toolTipData && (
          <TooltipPosition ref={toolTipRef}>
            <ToolTip activeData={toolTipData} locale={'de'} />
          </TooltipPosition>
        )}
        {!!(activeData && isMobile && activeMunicipalityId) && (
          <div
            ref={staticRef}
            style={{
              zIndex: 500,
              position: 'absolute',
              top: '-5px',
              left: 0,
            }}
          ></div>
        )}
      </MapWrapper>
    </>
  )
}
const TooltipPosition = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  pointer-events: none;
  z-index: 1000;
`

const MapWrapper = styled.div`
  transition: margin-bottom 0.3s ease-in-out;
  /* padding-bottom: 25px; */
  position: relative;
  .tooltip {
    z-index: 600;
  }
`
